import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { get, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useGetManagerChainQuery } from 'services/secure/org-view.api';
import { CHAIN_STATES, deriveChainState, toDisplayRows } from './manager-chain-state';

const drawerSx = { width: { xs: '100%', sm: 380 }, maxWidth: '100%', p: 2 };

// Indent one step per level so the chain reads as a hierarchy without pulling in a tree widget for
// what is a linear list. Capped so a deep chain cannot push the text off a narrow drawer.
const indentFor = (index) => ({ pl: Math.min(index, 6) * 1.5 });

const PersonRow = ({ person, index, showLine }) => (
  <ListItem disableGutters sx={{ ...indentFor(index), alignItems: 'flex-start', py: 0.5 }}>
    <ListItemText
      primary={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: person.isSubject ? 700 : 400 }}
            component="span"
          >
            {showLine ? '└ ' : ''}
            {person.displayName || person.email}
          </Typography>
        </Box>
      }
      secondary={
        <>
          {person.jobTitle}
          {person.jobTitle && person.department ? ' · ' : ''}
          {person.department}
        </>
      }
      secondaryTypographyProps={{ variant: 'caption' }}
    />
  </ListItem>
);

/**
 * Right-hand panel showing the managers above one person and their direct reports.
 *
 * Rendered once by ManagerChainProvider; open it from anywhere with
 * `useManagerChain().showManagerChain(email)`. The query is skipped until an email is set, so no
 * request is made until the panel is actually opened.
 */
const ManagerChainDrawer = ({ email, onClose }) => {
  const { t } = useTranslation();
  const open = !isEmpty(email);
  const { data, isLoading, isFetching } = useGetManagerChainQuery(email, { skip: !open });

  const state = deriveChainState({ isLoading: isLoading || isFetching, data });
  const rows = toDisplayRows(data);
  const reports = get(data, 'directReports', []);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={drawerSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">{t('orgView.managerChain.title')}</Typography>
          <IconButton onClick={onClose} size="small" aria-label={t('common.close')}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Typography variant="caption" color="text.secondary">
          {email}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {state === CHAIN_STATES.LOADING && <CircularProgress size={24} />}

        {state === CHAIN_STATES.NOT_IN_DIRECTORY && (
          <Typography variant="body2">{t('orgView.managerChain.notInDirectory')}</Typography>
        )}

        {state === CHAIN_STATES.ALONE && (
          <Typography variant="body2">{t('orgView.managerChain.alone')}</Typography>
        )}

        {state === CHAIN_STATES.READY && (
          <>
            {get(data, 'truncated') && (
              <Chip size="small" label={t('orgView.managerChain.truncated')} sx={{ mb: 1 }} />
            )}

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {t('orgView.managerChain.above')}
            </Typography>
            <List dense disablePadding>
              {rows.map((person, index) => (
                <PersonRow
                  key={person.email || `${person.displayName}-${index}`}
                  person={person}
                  index={index}
                  showLine={index > 0}
                />
              ))}
            </List>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2">
              {t('orgView.managerChain.reports', { count: reports.length })}
            </Typography>
            {isEmpty(reports) ? (
              <Typography variant="body2" color="text.secondary">
                {t('orgView.managerChain.noReports')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {reports.map((person, index) => (
                  <PersonRow
                    key={person.email || `${person.displayName}-${index}`}
                    person={person}
                    index={1}
                  />
                ))}
              </List>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default ManagerChainDrawer;
